import React, { useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { initials, roleLabel } from '../utils';
import { navForRole } from '../access';
import useTheme from '../hooks/useTheme';
import ErrorBoundary from './ErrorBoundary';

export default function Layout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);

  // The nav table and the rule that filters it now live in access.js, shared
  // with the route guard in App.js — a page restricted in one is restricted in
  // both.
  const allowedNav = navForRole(user?.role);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 56 : 220,
        background: 'var(--bg-1)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        flexShrink: 0, transition: 'width 0.2s',
        overflow: 'hidden',
      }}>
        {/* Logo — doubles as a home link back to the dashboard */}
        <Link
          to="/dashboard"
          title="Go to Dashboard"
          style={{
            padding: collapsed ? '18px 16px' : '18px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 10,
            minHeight: 60,
            textDecoration: 'none', color: 'inherit',
          }}
        >
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: 'var(--accent-dim)', border: '1px solid var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14,
          }}>🖥️</div>
          {!collapsed && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>AssetTrack</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>Athens Services</div>
            </div>
          )}
        </Link>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {allowedNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              // The active page is marked with a red bar as well as a fill.
              // A fill alone was carrying the whole signal, and on the light
              // surfaces it is a very quiet grey — the bar is what makes the
              // current page findable at a glance. Padding compensates for the
              // border so the labels stay aligned with the inactive ones.
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: isActive ? '9px 16px 9px 13px' : '9px 16px',
                borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                margin: '1px 8px', borderRadius: 'var(--radius-sm)',
                fontSize: 14, fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--text-0)' : 'var(--text-2)',
                background: isActive ? 'var(--bg-3)' : 'transparent',
                textDecoration: 'none', transition: 'all 0.12s',
                whiteSpace: 'nowrap',
              })}
            >
              <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div style={{
          padding: collapsed ? '12px 10px' : '12px 16px',
          borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: 'var(--accent-dim)', border: '1px solid var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600, color: 'var(--accent-text)',
          }}>
            {initials(user?.displayName || '')}
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.displayName}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{roleLabel(user?.role)}</div>
            </div>
          )}
          {!collapsed && (
            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
              {/* Names the destination rather than the current state — "Dark
                  mode" beside a moon is ambiguous about which way it goes. The
                  icon shows what you would switch to for the same reason. */}
              <button
                className="btn-icon"
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-pressed={theme === 'dark'}
                style={{ fontSize: 15, lineHeight: 1 }}
              >
                {/* ☼ rather than ☀: the latter renders as a faint dot at this
                    size in the system font stack. Both are outline glyphs from
                    the same Unicode block, so they sit with the ⎋ beside them
                    instead of clashing like a colour emoji would. */}
                {theme === 'dark' ? '☼' : '☾'}
              </button>
              <button className="btn-icon" onClick={logout} title="Sign out" aria-label="Sign out">
                ⎋
              </button>
            </div>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          className="btn-icon"
          onClick={() => setCollapsed(c => !c)}
          style={{ margin: '0 auto 8px', display: 'block' }}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? '→' : '←'}
        </button>
      </aside>

      {/* Main content.

          The boundary sits inside <main> rather than around the whole app so a
          page that throws leaves the sidebar — and with it the nav and the
          sign-out button — still on screen. Keying it to the pathname is what
          lets the next nav click clear the error instead of showing the
          fallback again. */}
      <main style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <ErrorBoundary resetKey={pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
